import { useCallback, useEffect, useState } from 'react';

type UseLoadScriptByUrlReturn = [boolean, string, string, () => void];

/**
 * Hook to dynamically load external scripts by URL.
 *
 * @param id - Unique identifier for the script element
 * @param url - URL of the script to load
 * @param callback - Optional callback to execute after script loads
 * @returns Tuple of [scriptLoaded, id, url, callback]
 */
export const useLoadScriptByUrl = (
  id: string,
  url: string,
  callback: () => void = () => {}
): UseLoadScriptByUrlReturn => {
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const onScriptLoad = useCallback(() => {
    setScriptLoaded(true);
  }, []);

  useEffect(() => {
    const isScriptExist = document.getElementById(id);
    if (!isScriptExist) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      script.id = id;
      script.onload = onScriptLoad;
      document.body.appendChild(script);
    } else if (callback) {
      callback();
    }
  }, [id, url, onScriptLoad, callback]);

  return [scriptLoaded, id, url, callback];
};
